import { Switch } from '@distribusion/owl-frontend-ui'
import cn from 'classnames'
import React, { useContext } from 'react'

import Money from '@components/Money'
import bem from '@lib/bem'
import { useTranslation } from '@lib/i18n'
import { FieldsetContext } from '@pages/context/fieldset'
import { useParams } from '@stores/params'

import '@pages/Checkout/SeatSelection/NewSeats/OptionalSeatSelectionToggle.scss'

type OptionalSeatSelectionToggleContext = 'all' | 'connection'

interface OptionalSeatSelectionToggleProps {
  ancillaries: Ancillary.Item[]
  enabled: boolean
  onChange: (enabled: boolean) => void
  context: OptionalSeatSelectionToggleContext
}

const bemClass = 'optional-seat-selection-toggle'

const PriceDisclaimer: React.FC<{ context: OptionalSeatSelectionToggleContext }> = ({ context }) => {
  const { t } = useTranslation()

  return context === 'connection' ? (
    <span className={cn(bem(bemClass, 'price-context'), 'body-12')}>
      {t('checkout.seats.ancillary_disclaimers.per_direction')}
    </span>
  ) : (
    <></>
  )
}

export const OptionalSeatSelectionToggle: React.FC<OptionalSeatSelectionToggleProps> = ({
  ancillaries,
  enabled,
  context,
  onChange,
}) => {
  const [{ currency }] = useParams()
  const { t } = useTranslation()
  const { isDisabled } = useContext(FieldsetContext)

  const totalPrice = ancillaries.reduce((sum, ancillary) => sum + ancillary.price.fractional, 0)
  const ancillaryCurrency = ancillaries[0]?.price.currency || currency

  return (
    <section data-tag="seat-selection-optional-switch" className={cn(bem(bemClass), 'row', 'normal', 'body-14')}>
      <div className="cell grow column">
        <div className="cell row items-center">
          <div
            className={cn(bem(bemClass, 'title'), 'cell', 'grow', 'bolder', 'basis-auto', 'body-16')}
            role="heading"
            aria-level={5}
          >
            {t('checkout.seats.optional_seat_selection')}
          </div>
          <div
            className={cn(
              bem(bemClass, 'price', { selected: true }),
              'bold',
              'body-16',
              'cell',
              'end',
              'no-grow',
              'basis-auto',
              'items-center',
            )}
          >
            <Money fractional={enabled ? totalPrice : 0} currency={ancillaryCurrency} />
          </div>
        </div>
        <div className="cell">
          <span className={cn(bem(bemClass, 'price'), 'body-16')}>
            <Money fractional={totalPrice} currency={ancillaryCurrency} />
          </span>{' '}
          <PriceDisclaimer context={context} />
        </div>
      </div>
      <div className="cell basis-auto no-shrink no-grow">
        <Switch checked={enabled} disabled={isDisabled} onChange={onChange} />
      </div>
    </section>
  )
}
